import { apiClient as api } from './apiClient';

// ═══════════════════════════════════════════════════════════════
// Email de bienvenida tras el alta de una oportunidad de venta a iniciativa del cliente (#254, #255):
// OportunidadesEmailBienvenidaController, api/oportunidades/venta/{id}/email-bienvenida. Recurso ACL:
// Oportunidades.Gestionar. Los emails van en el cuerpo, nunca en la URL.
// ═══════════════════════════════════════════════════════════════

/** GC_C_TIPO_INICIATIVA «Cliente»: solo estas ventas tienen email de bienvenida. */
export const INICIATIVA_CLIENTE = 452;

/** Los avisos de un destinatario: con cualquiera de ellos no se le envía. */
export const TEXTO_AVISO: Record<string, string> = {
  sin_email: 'No tenemos su email',
  sin_nombre: 'No tenemos su nombre',
  nombre_no_valido: 'Su nombre lleva algo que no son letras',
  ya_enviado: 'Ya se le envió el email de esta oportunidad',
  comunicacion_comercial_desactivada: 'Tiene las comunicaciones comerciales desactivadas',
  baja_brevo: 'Está dado de baja en Brevo',
  baja_no_comprobada: 'No se ha podido comprobar si está dado de baja en Brevo',
};

export interface DestinatarioBienvenida {
  idCliente?: number | null;
  nombre: string;
  email: string;
}

export interface DestinatarioBienvenidaVista {
  idCliente?: number | null;
  nombre: string;
  nombreCompleto?: string | null;
  email?: string | null;
  avisos: string[];
  enviable: boolean;
  asunto: string;
  html: string;
}

export interface EmailBienvenidaVistaPrevia {
  idOportunidad: number;
  de: { nombre: string; email?: string | null };
  asunto: string;
  modoPrueba: boolean;
  buzonPruebas?: string | null;
  destinatarios: DestinatarioBienvenidaVista[];
}

export type ResultadoEnvio = 'enviado' | 'error' | 'rechazado';

export interface ResultadoDestinatarioBienvenida {
  idCliente?: number | null;
  nombre: string;
  email: string;
  resultado: ResultadoEnvio;
  detalle?: string | null;
  idMensaje?: string | null;
  enviadoA?: string | null;
}

export interface EnvioBienvenidaResultado {
  modoPrueba: boolean;
  buzonPruebas?: string | null;
  resultados: ResultadoDestinatarioBienvenida[];
}

const ruta = (idOportunidad: number) => `/api/oportunidades/venta/${idOportunidad}/email-bienvenida`;

/** La vista previa para los titulares de la oportunidad. */
export const getVistaPreviaBienvenida = async (idOportunidad: number): Promise<EmailBienvenidaVistaPrevia> =>
  (await api.get<EmailBienvenidaVistaPrevia>(ruta(idOportunidad))).data;

/** La vista previa para unos destinatarios dados (por ejemplo, uno añadido a mano). */
export const postVistaPreviaBienvenida = async (
  idOportunidad: number, destinatarios: DestinatarioBienvenida[],
): Promise<EmailBienvenidaVistaPrevia> =>
  (await api.post<EmailBienvenidaVistaPrevia>(`${ruta(idOportunidad)}/vista-previa`, { destinatarios })).data;

/** Envía el email, uno por destinatario. */
export const enviarBienvenida = async (
  idOportunidad: number, destinatarios: DestinatarioBienvenida[],
): Promise<EnvioBienvenidaResultado> =>
  (await api.post<EnvioBienvenidaResultado>(`${ruta(idOportunidad)}/envio`, { destinatarios })).data;

// Un email corriente, con la misma regla que el servidor (DestinatariosBienvenidaRequestValidator).
const EMAIL = /^[^@\s<>()[\],;:"\\]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;

export const esEmailValido = (email: string): boolean => {
  const limpio = email.trim();
  return limpio.length > 0 && limpio.length <= 254 && EMAIL.test(limpio);
};

// Un nombre, con la misma regla que el servidor: letras (con acentos), espacios, ' - y ., con al menos una letra y sin
// un punto pegado a lo que sigue (#260): ni dominios («www.premios.es») ni nada parecido, pero sí iniciales («J. Antonio»).
const NOMBRE = /^(?=.*\p{L})(?!.*\.\S)[\p{L}\p{M} '.-]+$/u;

/** Un nombre que el servidor acepta: de como mucho 100 caracteres, solo con letras, espacios, ' - y .. */
export const esNombreValido = (nombre: string): boolean => {
  const limpio = nombre.trim();
  return limpio.length > 0 && limpio.length <= 100 && NOMBRE.test(limpio);
};
